import * as React from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  Link,
  useNavigate,
} from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Package,
  Briefcase,
  Settings,
  LogOut,
  UserCheck,
  PanelLeft,
  Bell,
  CreditCard,
  FileText,
  DollarSign,
  Mail,
  Shield,
  BarChart3,
  Database,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  AdminAuthProvider,
  useAdminAuth,
} from "@/components/auth/admin-auth-context";
import AdminLoginPortal from "@/components/auth/admin-login-portal";

// Import page components
import DashboardPage from "@/pages/DashboardPage";
import EnhancedDashboardPage from "@/pages/EnhancedDashboardPage";
import AgentsPage from "@/pages/AgentsPage";
import AgentDetailPage from "@/pages/AgentDetailPage";
import AgentsApprovalPage from "@/pages/AgentsApprovalPage";
import NewAgentPage from "@/pages/NewAgentPage";
import BookingsPage from "@/pages/BookingsPage";
import ContentPage from "@/pages/ContentPage";
import CustomersPage from "@/pages/CustomersPage";
import NotificationsPage from "@/pages/NotificationsPage";
import PackagesPage from "@/pages/PackagesPage";
import PackagesApprovalPage from "@/pages/PackagesApprovalPage";
import PayoutsPage from "@/pages/PayoutsPage";
import SecurityPage from "@/pages/SecurityPage";
import SettingsPage from "@/pages/SettingsPage";
import SubscriptionsPage from "@/pages/SubscriptionsPage";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import DatabaseStatusPage from "@/pages/DatabaseStatusPage";

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("React Error Boundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-4">
              The application encountered an error. Please refresh the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Refresh Page
            </button>
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm text-gray-500">
                Error Details
              </summary>
              <pre className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded overflow-auto">
                {this.state.error?.toString()}
              </pre>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const SuperAdminSidebar = ({ className }: { className?: string }) => {
  const location = useLocation();
  const { logout } = useAdminAuth();

  const handleLogout = () => {
    logout();
  };

  const navItems = [
    {
      href: "/dashboard",
      icon: LayoutDashboard,
      label: "Dashboard",
      description: "Overview & Analytics",
    },
    {
      href: "/enhanced-dashboard",
      icon: BarChart3,
      label: "Enhanced Dashboard",
      description: "Advanced Analytics & Management",
      badge: "NEW",
    },
    {
      href: "/agents",
      icon: UserCheck,
      label: "DMC Management",
      description: "Approve & Manage DMCs",
      badge: "3",
    },
    {
      href: "/subscriptions",
      icon: CreditCard,
      label: "Subscription Check",
      description: "Verify Agent Subscriptions",
      badge: "2",
    },
    {
      href: "/packages",
      icon: Package,
      label: "Package Moderation",
      description: "Review & Approve Packages",
      badge: "7",
    },
    {
      href: "/bookings",
      icon: Briefcase,
      label: "Booking Operations",
      description: "Manage All Bookings",
    },
    {
      href: "/payouts",
      icon: DollarSign,
      label: "Payout Management",
      description: "Process DMC Payouts",
      badge: "2",
    },
    {
      href: "/content",
      icon: FileText,
      label: "Content Management",
      description: "Banners & Static Pages",
    },
    {
      href: "/notifications",
      icon: Mail,
      label: "Notification Center",
      description: "Email Templates & Alerts",
    },
    {
      href: "/customers",
      icon: Users,
      label: "Customer Analytics",
      description: "User Management & Insights",
    },
    {
      href: "/settings",
      icon: Settings,
      label: "Platform Settings",
      description: "System Configuration",
    },
    {
      href: "/database-status",
      icon: Database,
      label: "Database Status",
      description: "Verify Supabase Setup",
    },
  ];

  return (
    <nav
      className={cn(
        "flex flex-col h-full bg-white border-r border-gray-200 shadow-sm",
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-center border-b border-gray-200 h-16 shrink-0 px-4 bg-gradient-to-r from-blue-600 to-indigo-600">
        <Link
          to="/dashboard"
          className="flex items-center gap-2 font-semibold text-white"
        >
          <Shield className="h-6 w-6" />
          <span>Super Admin</span>
        </Link>
      </div>

      {/* Navigation Items */}
      <div className="flex-1 overflow-y-auto py-4">
        <div className="space-y-1 px-3">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;

            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-3 text-sm transition-all hover:bg-gray-100",
                  isActive
                    ? "bg-blue-50 text-blue-700 border-r-2 border-blue-600 font-medium"
                    : "text-gray-700 hover:text-gray-900"
                )}
              >
                <Icon
                  className={cn(
                    "h-5 w-5",
                    isActive ? "text-blue-600" : "text-gray-500"
                  )}
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{item.label}</span>
                    {item.badge && (
                      <Badge
                        variant="secondary"
                        className="bg-red-100 text-red-700 text-xs"
                      >
                        {item.badge}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {item.description}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="border-t border-gray-200 p-4 space-y-2">
        <Button
          variant="ghost"
          className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </nav>
  );
};

function SuperAdminLayoutContent({ children }: { children: React.ReactNode }) {
  console.log("🏗️ SuperAdminLayoutContent rendering...");
  const [pendingNotifications] = React.useState(5);
  const { isAuthenticated, adminUser, logout, isLoading } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  console.log("🔐 Auth state:", { isAuthenticated, adminUser, isLoading });

  const handleLogout = () => {
    logout();
  };

  const handleNotifications = () => {
    navigate("/notifications");
  };

  const handleAccountSettings = () => {
    navigate("/account-settings");
  };

  const handleSecurity = () => {
    navigate("/security");
  };

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <div className="text-center">
            <p className="text-gray-600 font-medium">
              Loading Super Admin Dashboard
            </p>
            <p className="text-gray-500 text-sm">
              Please wait while we verify your credentials...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If not authenticated, show ONLY the login portal
  if (!isAuthenticated) {
    if (location.pathname === "/") {
      return <AdminLoginPortal />;
    }
    return <Navigate to="/" replace />;
  }

  // If authenticated, render full admin layout with sidebar and dashboard
  return (
    <div className="flex h-screen bg-gray-50">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block lg:w-72 lg:shrink-0">
        <SuperAdminSidebar />
      </div>

      {/* Main Content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Top Header */}
        <header className="flex h-16 items-center gap-4 border-b bg-white px-4 lg:px-6 shadow-sm">
          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="shrink-0 lg:hidden"
              >
                <PanelLeft className="h-5 w-5" />
                <span className="sr-only">Toggle navigation menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0">
              <SuperAdminSidebar className="w-full" />
            </SheetContent>
          </Sheet>

          {/* Breadcrumb */}
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-gray-900">
              Platform Administration
            </h1>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Notifications */}
            <Button
              variant="outline"
              size="icon"
              className="relative hover:bg-gray-100"
              onClick={handleNotifications}
              title="View notifications"
            >
              <Bell className="h-4 w-4" />
              {pendingNotifications > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs"
                >
                  {pendingNotifications}
                </Badge>
              )}
            </Button>

            {/* Admin Profile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="overflow-hidden rounded-full"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src="/avatars/admin.jpg" alt="Admin" />
                    <AvatarFallback className="bg-blue-600 text-white">
                      SA
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">Super Admin</span>
                    <span className="text-xs text-gray-500">
                      {adminUser?.email || "admin@roam.com"}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleAccountSettings}
                  className="cursor-pointer"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Account Settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleSecurity}
                  className="cursor-pointer"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Security
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600 cursor-pointer"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6 bg-gray-50">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
}

// Simple test component to isolate issues
function SimpleTestComponent() {
  return (
    <div className="min-h-screen bg-green-50 flex items-center justify-center">
      <div className="text-center p-8">
        <h1 className="text-4xl font-bold text-green-600 mb-4">🧪 Simple Test</h1>
        <p className="text-gray-600 mb-4">Testing basic component rendering...</p>
        <div className="space-y-4">
          <div className="p-4 bg-white rounded shadow">
            <h2 className="text-xl font-semibold">Basic Test</h2>
            <p>If you can see this, basic components work!</p>
          </div>
          <button 
            onClick={() => window.location.search = '?test=full'}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Try Full Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  console.log("🚀 App component rendering...");
  
  // Simple test to see if React is working
  if (window.location.search.includes('test=1')) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-4xl font-bold text-blue-600 mb-4">✅ React is Working!</h1>
          <p className="text-gray-600 mb-4">The basic React app is functioning correctly.</p>
          <button 
            onClick={() => window.location.search = ''}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Go to Admin App
          </button>
        </div>
      </div>
    );
  }
  
  // Simple component test
  if (window.location.search.includes('test=simple')) {
    return <SimpleTestComponent />;
  }
  
  // Minimal working version for debugging
  if (window.location.search.includes('debug=1')) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-4xl font-bold text-green-600 mb-4">🔧 Debug Mode</h1>
          <p className="text-gray-600 mb-4">Testing minimal React components...</p>
          <div className="space-y-4">
            <div className="p-4 bg-white rounded shadow">
              <h2 className="text-xl font-semibold">Basic Components Test</h2>
              <p>If you can see this, React is working!</p>
            </div>
            <button 
              onClick={() => window.location.search = ''}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Try Full Admin App
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  try {
    console.log("🔧 Attempting to render full admin app...");
    
    // Test if we can render basic components first
    if (window.location.search.includes('test=basic')) {
      return (
        <div className="min-h-screen bg-yellow-50 flex items-center justify-center">
          <div className="text-center p-8">
            <h1 className="text-4xl font-bold text-yellow-600 mb-4">🔧 Basic Component Test</h1>
            <p className="text-gray-600 mb-4">Testing if basic components can render...</p>
            <div className="space-y-4">
              <div className="p-4 bg-white rounded shadow">
                <h2 className="text-xl font-semibold">Basic Test</h2>
                <p>If you can see this, basic components work!</p>
              </div>
              <button 
                onClick={() => window.location.search = ''}
                className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
              >
                Try Full Admin App
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    // Test individual components one by one
    if (window.location.search.includes('test=dashboard')) {
      console.log("🧪 Testing DashboardPage component...");
      try {
        return (
          <div className="min-h-screen bg-blue-50 p-8">
            <h1 className="text-3xl font-bold mb-4">🧪 Testing DashboardPage</h1>
            <DashboardPage />
          </div>
        );
      } catch (error) {
        console.error("❌ DashboardPage error:", error);
        return (
          <div className="min-h-screen bg-red-50 flex items-center justify-center">
            <div className="text-center p-8">
              <h1 className="text-4xl font-bold text-red-600 mb-4">❌ DashboardPage Failed</h1>
              <p className="text-gray-600 mb-4">Error in DashboardPage component:</p>
              <pre className="text-sm text-red-600 bg-red-100 p-4 rounded overflow-auto max-w-2xl">
                {error?.toString()}
              </pre>
              <button 
                onClick={() => window.location.search = ''}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 mt-4"
              >
                Go Back
              </button>
            </div>
          </div>
        );
      }
    }
    
    if (window.location.search.includes('test=bookings')) {
      console.log("🧪 Testing BookingsPage component...");
      try {
        return (
          <div className="min-h-screen bg-green-50 p-8">
            <h1 className="text-3xl font-bold mb-4">🧪 Testing BookingsPage</h1>
            <BookingsPage />
          </div>
        );
      } catch (error) {
        console.error("❌ BookingsPage error:", error);
        return (
          <div className="min-h-screen bg-red-50 flex items-center justify-center">
            <div className="text-center p-8">
              <h1 className="text-4xl font-bold text-red-600 mb-4">❌ BookingsPage Failed</h1>
              <p className="text-gray-600 mb-4">Error in BookingsPage component:</p>
              <pre className="text-sm text-red-600 bg-red-100 p-4 rounded overflow-auto max-w-2xl">
                {error?.toString()}
              </pre>
              <button 
                onClick={() => window.location.search = ''}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 mt-4"
              >
                Go Back
              </button>
            </div>
          </div>
        );
      }
    }
    
    return (
      <AdminAuthProvider>
        <ErrorBoundary>
          <SuperAdminLayoutContent>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/simple" element={<SimpleTestComponent />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route
                path="/enhanced-dashboard"
                element={<EnhancedDashboardPage />}
              />
              <Route path="/agents" element={<AgentsPage />} />
              <Route path="/agents/:id" element={<AgentDetailPage />} />
              <Route path="/agents/approval" element={<AgentsApprovalPage />} />
              <Route path="/agents/new" element={<NewAgentPage />} />
              <Route path="/bookings" element={<BookingsPage />} />
              <Route path="/content" element={<ContentPage />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/packages" element={<PackagesPage />} />
              <Route path="/packages/approval" element={<PackagesApprovalPage />} />
              <Route path="/payouts" element={<PayoutsPage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/subscriptions" element={<SubscriptionsPage />} />
              <Route path="/account-settings" element={<AccountSettingsPage />} />
              <Route path="/database-status" element={<DatabaseStatusPage />} />
            </Routes>
          </SuperAdminLayoutContent>
        </ErrorBoundary>
      </AdminAuthProvider>
    );
  } catch (error) {
    console.error("❌ Error in App component:", error);
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-4xl font-bold text-red-600 mb-4">❌ App Error</h1>
          <p className="text-gray-600 mb-4">The admin app encountered an error:</p>
          <pre className="text-sm text-red-600 bg-red-100 p-4 rounded overflow-auto max-w-2xl">
            {error?.toString()}
          </pre>
          <button 
            onClick={() => window.location.search = ''}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 mt-4"
          >
            Try Simple Test
          </button>
        </div>
      </div>
    );
  }
}

export default App;
